import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { DialogService } from '@app/core/services/dialog/dialog.service';
import { SnackbarService } from '@app/core/services/snackbar/snackbar.service';
import { EntitySelectionModel } from '@components/data/data-table-new/entitySelectionModel';
import { InputSearchComponent } from '@components/form-controls/inputs/input-search/input-search.component';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { GetHierarchyDto, NodeDto, TreeNode } from '../../models/hierarchy.model';
import { HierarchyService } from '../../services/hierarchy.service';
import { ViewTreeService } from '../../services/tree/node-tree.service';

@Component({
  selector: 'vfs-hierarchy-select-node',
  templateUrl: './hierarchy-select-node.component.html',
  styleUrls: ['./hierarchy-select-node.component.scss'],
  providers: [ViewTreeService]
})
export class HierarchySelectNodeComponent implements OnInit {
  @Input() hierarchy: GetHierarchyDto;
  // IF FILTER
  @Input() filterMode = false;
  @Input() selection: EntitySelectionModel<any> = new EntitySelectionModel();

  activeNode: TreeNode;
  expanded: boolean;
  rootNode: NodeDto = null;
  searchValue: string = null;
  rootNodeSubscription: Subscription;
  loading = false;

  obs$: Observable<NodeDto>;

  @ViewChild('searchInput') searchInput: InputSearchComponent;

  constructor(
    protected treeService: ViewTreeService,
    protected route: ActivatedRoute,
    protected dialog: MatDialog,
    protected hierarchyService: HierarchyService,
    protected snackBarService: SnackbarService,
    protected translateService: TranslateService,
    protected dialogService: DialogService
  ) {}

  ngOnInit(): void {
    if (this.filterMode) {
      if (this.selection.selected) {
        this.activeNode = this.selection.selected[0];
      }
    }

    this.obs$ = this.hierarchyService.getHierarchyRootNode(this.hierarchy.id).pipe(
      catchError(error => {
        this.snackBarService.openSnackBar(error.message, 'warning');
        return throwError(error);
      }),
      tap(rootNode => {
        this.loading = true;
        this.rootNode = rootNode;
        this.treeService.initialize();
        this.treeService.dataSource.initialize(rootNode).then(res => (this.loading = false));
      })
    );
    this.expandedNode();
  }

  /**
   * Change search input
   * @param event event
   */
  changeSearchInput(event: string) {
    if (event.length > 2) {
      this.loading = true;
      this.searchValue = event;
      this.treeService.initialize();
      this.treeService.dataSource.initializeWithSearch(this.rootNode, event).then(res => (this.loading = false));
    } else if (event.length === 0 && event.length < this.searchValue.length) {
      this.loading = true;
      this.searchValue = null;
      this.treeService.initialize();
      this.treeService.dataSource.initialize(this.rootNode).then(res => (this.loading = false));
    }
  }

  selectNode(node: TreeNode) {
    if (this.filterMode) {
      this.activeNode = node;
    }
    this.selection.clear();
    this.selection.select(node.item);
  }

  checkError(error: any) {
    if (error.status === 404) {
      this.snackBarService.openSnackBar(error.message, 'warning');
    } else {
      return throwError(error);
    }
  }

  /** searchInput flag */
  searchHasValue(): boolean {
    return this.searchInput ? this.searchInput.inputRef.nativeElement.value !== '' : false;
  }

  hasChild(node: TreeNode) {
    return this.treeService.isExpandable(node);
  }

  loadMore(node: TreeNode) {
    this.treeService.dataSource.loadMore(node);
  }

  /** Check if the node is expanded and enable the collapse button */
  expandedNode() {
    var count = 0;
    this.treeService.dataSource.dataTree.forEach(node => {
      if (this.treeService.treeControl.isExpanded(node)) {
        count++;
      }
    });
    count !== 0 ? (this.expanded = true) : (this.expanded = false);
  }

  getEmptyStateText(): string {
    return this.searchHasValue() ? '' : 'hierarchy.detail.emptyHierarchy';
  }

  checkDatasourceContainsData() {
    return (
      !this.treeService.dataSource ||
      this.treeService.dataSource.data.length === 0 || // Searching not have nodes
      (this.treeService.dataSource.data.length > 0 && // Empty has root node
        this.treeService.dataSource.data[0].childrenCount === 0 &&
        this.treeService.dataSource.data[0].item.nodeTypeId === this.rootNode.nodeTypeId)
    );
  }
}
